import { cn } from '@skin-seoul/design-system';
import React from 'react';
import type { Review, ReviewImage } from '..';
import { StarIcon } from '../assets/icons/StarIcon';
import { formatReviewRating } from '@skin-seoul/utils';

interface ReviewHeaderProps {
  averageRating: number;
  totalReviews: number;
  ratingDistribution: {
    [key: number]: number;
  };
  onlyScore?: boolean;
  onlyDistribution?: boolean;
  customerImages?: ReviewImage[];
  reviews?: Review[];
  onViewAllImages?: () => void;
  onWriteReview: () => void;
  isWriteReviewDisabled: boolean;
}

const ProgressBar = ({ percentage, className }: { percentage: number; className?: string }) => (
  <div
    className={cn(
      'grow w-full tablet:w-[357px] h-[5px] bg-neutral-100 rounded-[4px] overflow-hidden relative',
      "after:content-[''] after:absolute after:left-0 after:top-0 after:h-full after:bg-neutral-700 after:rounded-[4px]",
      className,
    )}
    style={{ ['--tw-after-w' as string]: `${percentage}%` }}
  >
    <div className="h-full bg-neutral-700 rounded-[4px]" style={{ width: `${percentage}%` }} />
  </div>
);

export const ReviewHeader = ({ averageRating, totalReviews, ratingDistribution }: ReviewHeaderProps) => {
  const getDistributionPercentage = (rating: number): number => {
    const count = ratingDistribution[rating] || 0;
    return (count / totalReviews) * 100;
  };

  return (
    <div className="flex flex-col gap-6 tablet:gap-4 items-start">
      <h6 className="text-paragraph-medium tablet:text-heading-h6 font-medium text-neutral-800">Summary</h6>
      <div className="flex flex-col tablet:flex-row gap-6 justify-between w-full">
        <div className="flex flex-col gap-2 items-start">
          <div className="flex flex-row gap-1 items-center">
            <StarIcon width={36} height={36} />
            <p className="text-display-large font-bold text-primary-400">{formatReviewRating(averageRating)}</p>
          </div>
          {totalReviews && (
            <p className="text-label-medium font-medium text-neutral-500">
              {totalReviews === 1 ? `${totalReviews} review` : `${totalReviews} reviews`}
            </p>
          )}
        </div>

        <div className="w-full tablet:w-fit">
          <div className="flex flex-col gap-3 items-start w-full">
            {Object.entries(ratingDistribution)
              .sort(([a], [b]) => Number(b) - Number(a))
              .map(([rating]) => (
                <div key={rating} className="flex flex-row gap-3 items-center w-full">
                  <p className="text-paragraph-small font-medium text-neutral-700 shrink-0">{rating} Star</p>
                  <ProgressBar percentage={getDistributionPercentage(Number(rating))} />
                  <p className="text-paragraph-medium font-medium text-neutral-700 shrink-0 w-[45px] text-center">
                    {parseInt(getDistributionPercentage(Number(rating)).toString(), 10)}%
                  </p>
                </div>
              ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReviewHeader;
